import { Dispatch, SetStateAction, useRef, useState, useEffect } from "react";
import { Close, Check, GroupOutlined } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography as T,
  menuItemClasses,
  Typography,
} from "@mui/material";
import User, { UserGroup } from "admin/types/interfaces/user";
import useCollaborators from "datasets/hooks/useCollaborators";
import usePreviewMode from "datasets/hooks/usePreviewMode";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { DatasetGroup, DatasetUser } from "datasets/types/interfaces/collaborators";
import Dataset from "datasets/types/interfaces/dataset";
import UserAndGroupAutocomplete, {
  CollaboratorType,
} from "~/components/UserAndGroupSearchAutocomplete";
import useUser from "~/contexts/user";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const NO_ACCESS = "no-access";

type DatasetRole = DatasetAccessLevel | typeof NO_ACCESS;

const buildDisplayDatasetRole = (showPreview: boolean) => {
  const roles: Record<DatasetRole, { title: string; description: string }> = {
    [DatasetAccessLevel.OWNER]: {
      title: "Owner",
      description: "Full access with ability to modify data assets",
    },
    [DatasetAccessLevel.ANALYZE]: {
      title: "Analyze",
      description: "Read-only access to all data assets",
    },
  } as Record<DatasetRole, { title: string; description: string }>;

  if (showPreview) {
    roles[DatasetAccessLevel.PREVIEW] = {
      title: "Preview",
      description: "Read-only access to limited data assets",
    };
  }

  // keeping it here to maintain order of roles
  roles[NO_ACCESS] = {
    title: "Remove",
    description: "",
  };

  return roles;
};

const CollaboratorGroupRow = ({
  collaboratorGroup,
  disabled,
  handleChange,
  displayRoles,
}: {
  collaboratorGroup: DatasetGroup;
  disabled: boolean;
  handleChange: (group: DatasetGroup, access_level: DatasetRole) => void;
  displayRoles: ReturnType<typeof buildDisplayDatasetRole>;
}) => {
  const { name, access_level, display_name } = collaboratorGroup;
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
      <Box display="flex" gap={1.5} alignItems="center">
        <Avatar sx={{ height: 30, width: 30, backgroundColor: "grey.200" }}>
          <GroupOutlined sx={{ fontSize: 16, color: "grey.900" }} />
        </Avatar>

        <T variant="body1" color="grey.800" fontWeight="medium" fontSize={14}>
          {display_name ?? name}
        </T>
      </Box>
      <Select
        value={access_level ?? NO_ACCESS}
        displayEmpty
        onChange={(event) => handleChange(collaboratorGroup, event.target.value as DatasetRole)}
        disabled={disabled}
        sx={{
          marginLeft: "auto",
          width: 120,
        }}
        variant="outlined"
        size="small"
        renderValue={(value: DatasetRole) => displayRoles[value].title}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        }}
      >
        {(Object.keys(displayRoles) as Array<DatasetRole>).map((datasetPermission) => (
          <MenuItem
            key={datasetPermission}
            value={datasetPermission}
            color="primary"
            sx={{
              ...(datasetPermission === NO_ACCESS && {
                color: "error.main",
                borderTop: "1px solid",
                borderColor: "divider",
                mt: 1,
                pt: 1,
                [`&.${menuItemClasses.root}:hover`]: {
                  backgroundColor: "error.main",
                },
              }),
            }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              {access_level === datasetPermission && <Check sx={{ fontSize: 16 }} />}
              <Stack sx={{ ml: access_level !== datasetPermission ? 3 : 0 }}>
                <T variant="body2" fontWeight="medium" sx={{ color: "inherit" }}>
                  {displayRoles[datasetPermission].title}
                </T>
                {displayRoles[datasetPermission].description && (
                  <T variant="caption" display="block" sx={{ color: "inherit" }}>
                    {displayRoles[datasetPermission].description}
                  </T>
                )}
              </Stack>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const CollaboratorUserRow = ({
  collaboratorUser,
  handleChange,
  disabled,
  displayRoles,
}: {
  collaboratorUser: DatasetUser;
  handleChange: (user: DatasetUser, access_level: DatasetRole) => void;
  disabled: boolean;
  displayRoles: ReturnType<typeof buildDisplayDatasetRole>;
}) => {
  const user = useUser();
  const { access_level } = collaboratorUser;

  const filterRoleOptions = (datasetPermission: DatasetRole) => {
    if (user?.email.toLowerCase() === collaboratorUser?.email.toLowerCase()) {
      return datasetPermission === access_level;
    }
    return true;
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
      <Tooltip arrow title={collaboratorUser?.email} placement="right" enterDelay={500}>
        <Box display="flex" gap={1.5} alignItems="center">
          <Avatar src={collaboratorUser.image_url} sx={{ height: 30, width: 30 }}>
            {collaboratorUser.full_name?.[0]}
            {collaboratorUser.full_name?.split(" ")[1]?.[0]}
          </Avatar>

          <T variant="body1" color="grey.800" fontWeight="medium" fontSize={14}>
            {collaboratorUser.full_name ?? collaboratorUser.email}
          </T>
        </Box>
      </Tooltip>
      <Select
        value={access_level ?? NO_ACCESS}
        displayEmpty
        onChange={({ target }) => handleChange(collaboratorUser, target.value as DatasetRole)}
        disabled={disabled}
        sx={{
          marginLeft: "auto",
          width: 120,
        }}
        variant="outlined"
        size="small"
        renderValue={(value: DatasetRole) => displayRoles[value].title}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        }}
      >
        {(Object.keys(displayRoles) as Array<DatasetRole>)
          .filter(filterRoleOptions)
          .map((datasetPermission) => (
            <MenuItem
              key={datasetPermission}
              value={datasetPermission}
              color="primary"
              sx={{
                ...(datasetPermission === NO_ACCESS && {
                  color: "error.main",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  mt: 1,
                  pt: 1,
                  [`&.${menuItemClasses.root}:hover`]: {
                    backgroundColor: "error.main",
                  },
                }),
              }}
            >
              <Box display="flex" alignItems="center" gap={1} width="100%">
                {access_level === datasetPermission && <Check sx={{ fontSize: 16 }} />}
                <Stack sx={{ ml: access_level !== datasetPermission ? 3 : 0 }}>
                  <T variant="body2" fontWeight="medium" sx={{ color: "inherit" }}>
                    {displayRoles[datasetPermission].title}
                  </T>
                  {displayRoles[datasetPermission].description && (
                    <T variant="caption" display="block" sx={{ color: "inherit" }}>
                      {displayRoles[datasetPermission].description}
                    </T>
                  )}
                </Stack>
              </Box>
            </MenuItem>
          ))}
      </Select>
    </Box>
  );
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  dataset?: Dataset;
  datasetId?: string;
}

const ModalBody = ({
  onClose,
  confirmRemovalCollaborator,
  setConfirmRemovalCollaborator,
  dataset,
  datasetId,
}: ModalProps & {
  confirmRemovalCollaborator: { user?: DatasetUser; group?: DatasetGroup } | null;
  setConfirmRemovalCollaborator: Dispatch<
    SetStateAction<{ user?: DatasetUser; group?: DatasetGroup } | null>
  >;
}) => {
  // Preview access level requires both: feature flag enabled AND dataset has preview mode enabled
  const isPreviewModeFeatureEnabled = usePreviewMode();
  const showPreview = isPreviewModeFeatureEnabled && (dataset?.previewEnabled ?? false);
  const DISPLAY_DATASET_ROLE = buildDisplayDatasetRole(showPreview);
  const DEFAULT_PERMISSION = showPreview ? DatasetAccessLevel.PREVIEW : DatasetAccessLevel.ANALYZE;

  const [savingUpdate, setSavingUpdate] = useState(false);

  const { data: datasetUsers, isLoading, mutate } = useCollaborators();
  const [error, setError] = useState<string>("");
  const [collaboratorToInvite, setCollaboratorToInvite] = useState<{
    uniqueId: string;
    type: CollaboratorType;
    metadata: User | UserGroup;
    label: string;
  } | null>(null);
  const currentUser = useUser();

  const collaboratorUsers = (datasetUsers?.users ?? []).sort((a, b) => {
    const nameA = a.full_name?.toLowerCase() || a.email.toLowerCase();
    const nameB = b.full_name?.toLowerCase() || b.email.toLowerCase();
    return nameA.localeCompare(nameB);
  });
  const collaboratorGroups = datasetUsers?.groups ?? [];
  /**
   * Input Refs for managing focus state of input and invite buttons
   */
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inviteRef = useRef<HTMLButtonElement | null>(null);

  const ownerCount = [
    ...collaboratorUsers.filter((user) => user.access_level === DatasetAccessLevel.OWNER),
    ...collaboratorGroups.filter((group) => group.access_level === DatasetAccessLevel.OWNER),
  ].length;

  const isOnlyOwner = (collaborator: DatasetUser | DatasetGroup) => {
    return ownerCount === 1 && collaborator.access_level === DatasetAccessLevel.OWNER;
  };

  const mutateCollaboratorUsers = (collaborator: DatasetUser, access_level: DatasetRole) => {
    if (access_level !== NO_ACCESS) {
      mutate(
        (currentData) => {
          if (!currentData) {
            return currentData;
          }
          const existingUserIndex = (currentData.users || []).findIndex(
            (user) => user.email === collaborator.email
          );
          if (existingUserIndex >= 0) {
            // Update existing user's access level in place
            const updatedUsers = [...(currentData.users || [])];
            updatedUsers[existingUserIndex] = {
              ...updatedUsers[existingUserIndex],
              access_level: access_level,
            };
            return {
              ...currentData,
              users: updatedUsers,
            };
          }
          // Add new user to the end of the list
          const newUser: DatasetUser = {
            email: collaborator.email,
            full_name: collaborator.full_name || "",
            image_url: collaborator.image_url || "",
            access_level: access_level,
          };
          return {
            ...currentData,
            users: [...(currentData.users || []), newUser],
          };
        },
        { revalidate: false }
      );
    } else {
      // Remove user from the list
      mutate(
        (currentData) => {
          if (!currentData) {
            return currentData;
          }
          return {
            ...currentData,
            users: (currentData.users || []).filter((user) => user.email !== collaborator.email),
          };
        },
        { revalidate: false }
      );
    }
  };

  const mutateCollaboratorGroups = (collaborator: DatasetGroup, access_level: DatasetRole) => {
    if (access_level !== NO_ACCESS) {
      mutate(
        (currentData) => {
          if (!currentData) {
            return currentData;
          }
          const existingGroupIndex = (currentData.groups || []).findIndex(
            (group) => group.name === collaborator.name
          );
          if (existingGroupIndex >= 0) {
            // Update existing group's access level in place
            const updatedGroups = [...(currentData.groups || [])];
            updatedGroups[existingGroupIndex] = {
              ...updatedGroups[existingGroupIndex],
              access_level: access_level,
            };
            return {
              ...currentData,
              groups: updatedGroups,
            };
          }
          // Add new group to the end of the list
          const newGroup: DatasetGroup = {
            name: collaborator.name,
            display_name: collaborator.display_name,
            is_internal: collaborator.is_internal,
            access_level: access_level,
            users: [],
          };
          return {
            ...currentData,
            groups: [...(currentData.groups || []), newGroup],
          };
        },
        { revalidate: false }
      );
    } else {
      // Remove group from the list
      mutate(
        (currentData) => {
          if (!currentData) {
            return currentData;
          }
          return {
            ...currentData,
            groups: (currentData.groups || []).filter((group) => group.name !== collaborator.name),
          };
        },
        { revalidate: false }
      );
    }
  };
  const handleShareDataset = async (
    collaborator: { user?: DatasetUser; group?: DatasetGroup },
    access_level: DatasetRole
  ) => {
    setSavingUpdate(true);
    setError("");

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(collaborator.user && { email: collaborator.user.email }),
          ...(collaborator.group && { group: collaborator.group.name }),
          access_level: access_level === NO_ACCESS ? null : access_level,
        }),
      });

      if (!resp.ok) {
        throw new Error("Unable to update dataset access");
      }

      trackEvent("dataset_access_shared", {
        dataset_id: datasetId,
        ...(collaborator.user && { collaborator_email: collaborator.user.email }),
        ...(collaborator.group && { collaborator_group: collaborator.group.name }),
        access_level: access_level === NO_ACCESS ? "removed" : access_level,
        action: access_level === NO_ACCESS ? "remove_access" : "grant_access",
      });

      collaborator.user && mutateCollaboratorUsers(collaborator.user, access_level);
      collaborator.group && mutateCollaboratorGroups(collaborator.group, access_level);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setError(e.message);
    }

    setSavingUpdate(false);
  };

  useEffect(() => {
    /**
     * When a user is selected from the autocomplete list,
     *  pass keyboard focus to the invite button.
     */
    inviteRef?.current?.focus();
  }, [collaboratorToInvite]);

  const clearAutocomplete = () => {
    setCollaboratorToInvite(null);
    inputRef?.current?.focus();
  };

  return (
    <>
      <DialogTitle>
        Share This Dataset
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Box position="sticky" bgcolor="common.white" zIndex={1} top={0} px={3}>
        <Box display="flex" alignItems="center" width="100%" gap={1}>
          <UserAndGroupAutocomplete
            value={collaboratorToInvite}
            onChange={setCollaboratorToInvite}
            excludedEmails={collaboratorUsers.map((user) => user.email)}
            excludedGroupNames={collaboratorGroups.map((group) => group.name)}
            inputRef={inputRef}
            setError={setError}
            activeOnly
          />
          <Button
            variant="contained"
            ref={inviteRef}
            disabled={!collaboratorToInvite}
            loading={savingUpdate}
            sx={{
              height: 40,
            }}
            onClick={() => {
              if (collaboratorToInvite) {
                handleShareDataset(
                  {
                    ...(collaboratorToInvite.type === CollaboratorType.USER && {
                      user: {
                        email: (collaboratorToInvite.metadata as User).email,
                        full_name: (collaboratorToInvite.metadata as User).full_name,
                        image_url: (collaboratorToInvite.metadata as User).image_url,
                        access_level: DEFAULT_PERMISSION,
                      },
                    }),
                    ...(collaboratorToInvite.type === CollaboratorType.GROUP && {
                      group: {
                        name: (collaboratorToInvite.metadata as UserGroup).name,
                        display_name: (collaboratorToInvite.metadata as UserGroup).display_name,
                        is_internal: (collaboratorToInvite.metadata as UserGroup).is_internal,
                        access_level: DEFAULT_PERMISSION,
                        users: [],
                      },
                    }),
                  },
                  DEFAULT_PERMISSION
                );
              }
              clearAutocomplete();
            }}
          >
            Invite
          </Button>
        </Box>
      </Box>
      <DialogContent
        sx={{
          pt: 1,
          height: 350,
          gap: 2,
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {isLoading ? (
          <Stack py={4} alignItems="center">
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <Stack gap={1.5}>
            <Divider sx={{ mt: 1 }} />
            <Typography color="text.secondary" fontSize={14}>
              Who has access
            </Typography>
            {collaboratorUsers.map((collaboratorUser) => (
              <CollaboratorUserRow
                key={collaboratorUser.email}
                collaboratorUser={collaboratorUser}
                handleChange={async (user, access_level) => {
                  if (access_level !== NO_ACCESS) {
                    handleShareDataset({ user }, access_level);
                  } else {
                    setConfirmRemovalCollaborator({ user });
                  }
                }}
                disabled={
                  collaboratorUser.email === currentUser?.email || isOnlyOwner(collaboratorUser)
                }
                displayRoles={DISPLAY_DATASET_ROLE}
              />
            ))}
            {collaboratorGroups.map((group) => (
              <CollaboratorGroupRow
                key={group.name}
                collaboratorGroup={group}
                handleChange={async (group, access_level) => {
                  if (access_level !== NO_ACCESS) {
                    handleShareDataset({ group }, access_level);
                  } else {
                    setConfirmRemovalCollaborator({ group });
                  }
                }}
                disabled={isOnlyOwner(group)}
                displayRoles={DISPLAY_DATASET_ROLE}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <Dialog open={!!confirmRemovalCollaborator} hideBackdrop transitionDuration={0}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this {confirmRemovalCollaborator?.user ? "user" : "group"}{" "}
          from the dataset?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmRemovalCollaborator(null)}
            size="medium"
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmRemovalCollaborator) {
                await handleShareDataset(confirmRemovalCollaborator, NO_ACCESS);
              }
              setConfirmRemovalCollaborator(null);
            }}
            loading={savingUpdate}
            color="error"
            variant="contained"
            size="medium"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CollaboratorsModal = (props: ModalProps) => {
  const [confirmRemovalCollaborator, setConfirmRemovalCollaborator] = useState<{
    user?: DatasetUser;
    group?: DatasetGroup;
  } | null>(null);
  return (
    <Dialog
      fullWidth
      open={props.open}
      onClose={props.onClose}
      disableRestoreFocus={import.meta.env.DEV}
      PaperProps={{
        sx: {
          // Temporarily hide while removal confirmation open
          display: confirmRemovalCollaborator ? "none" : "block",
        },
      }}
    >
      <ModalBody
        {...props}
        confirmRemovalCollaborator={confirmRemovalCollaborator}
        setConfirmRemovalCollaborator={setConfirmRemovalCollaborator}
      />
    </Dialog>
  );
};

export default CollaboratorsModal;
